import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import {
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import useBlog from "src/hooks/useBlog";
import { useSelector } from "src/store";

const STORAGE_PREFIX = "blog_announcement_state";

const getStorageKey = ({ customerId, userId, announcementId }) =>
  `${STORAGE_PREFIX}:${customerId}:${userId}:${announcementId}`;

const readAnnouncementState = (key) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch (_error) {
    return {};
  }
};

const writeAnnouncementState = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // ignore localStorage failures
  }
};

const buildHiddenUntil = (hours) => {
  const safeHours = Math.max(0, Number(hours || 0));
  if (safeHours <= 0) {
    return null;
  }
  return Date.now() + safeHours * 60 * 60 * 1000;
};

const GlobalAnnouncementCenter = () => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.users.user);
  const currentCustomer = useSelector(
    (state) => state.customers.currentCustomer,
  );
  const adminMode = useSelector((state) => state.settings.adminMode);
  const location = useLocation();
  const { announcement } = useBlog();
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [open, setOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const seenAnnouncementRef = useRef(null);

  const storageKey = useMemo(() => {
    if (!user?.id || !currentCustomer?.id || !currentAnnouncement?.id) {
      return null;
    }
    return getStorageKey({
      customerId: currentCustomer.id,
      userId: user.id,
      announcementId: currentAnnouncement.id,
    });
  }, [currentAnnouncement?.id, currentCustomer?.id, user?.id]);

  const currentModuleKey = useMemo(() => {
    const pathParts = String(location.pathname || "")
      .split("/")
      .filter(Boolean);

    if (pathParts.length >= 2 && currentCustomer?.shortname) {
      if (pathParts[0] === currentCustomer.shortname) {
        return pathParts[1] || "";
      }
    }

    return pathParts[0] || "";
  }, [currentCustomer?.shortname, location.pathname]);

  const openAnnouncement = (announcementData) => {
    if (!announcementData?.id) {
      return;
    }
    setCurrentAnnouncement(announcementData);
    setSecondsLeft(Math.max(0, Number(announcementData.dismiss_seconds || 0)));
    setOpen(true);
  };

  useEffect(() => {
    seenAnnouncementRef.current = null;
  }, [currentCustomer?.id, user?.id]);

  useEffect(() => {
    let active = true;

    const loadAnnouncement = async () => {
      if (!user?.id || !currentCustomer?.id) {
        setCurrentAnnouncement(null);
        setOpen(false);
        return;
      }

      const announcementData = await announcement.current({
        mode: adminMode ? "admin" : "user",
        moduleKey: currentModuleKey,
      });

      if (!active || !announcementData?.id) {
        setCurrentAnnouncement(null);
        setOpen(false);
        return;
      }

      const key = getStorageKey({
        customerId: currentCustomer.id,
        userId: user.id,
        announcementId: announcementData.id,
      });
      const persisted = readAnnouncementState(key);
      const now = Date.now();
      const hiddenUntil = Number(persisted.hiddenUntil || 0);

      if (hiddenUntil > now) {
        setCurrentAnnouncement(announcementData);
        setOpen(false);
        return;
      }

      if (seenAnnouncementRef.current === announcementData.id) {
        setCurrentAnnouncement(announcementData);
        return;
      }

      const nextState = {
        ...persisted,
        viewCount: Number(persisted.viewCount || 0) + 1,
        lastShownAt: now,
      };
      writeAnnouncementState(key, nextState);
      seenAnnouncementRef.current = announcementData.id;
      openAnnouncement(announcementData);
    };

    loadAnnouncement();
    return () => {
      active = false;
    };
  }, [adminMode, currentCustomer?.id, currentModuleKey, user?.id]);

  useEffect(() => {
    if (!open || secondsLeft <= 0) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [open, secondsLeft]);

  const handleClose = ({ hideForPeriod = false } = {}) => {
    if (storageKey && currentAnnouncement?.id) {
      const persisted = readAnnouncementState(storageKey);
      const nextState = {
        ...persisted,
        lastDismissedAt: Date.now(),
      };

      const reachedMaxViews =
        Number(currentAnnouncement.max_views || 0) > 0 &&
        Number(persisted.viewCount || 0) >=
          Number(currentAnnouncement.max_views || 0);

      if (hideForPeriod || reachedMaxViews) {
        nextState.hiddenUntil = buildHiddenUntil(
          currentAnnouncement.hide_for_hours,
        );
      }
      writeAnnouncementState(storageKey, nextState);
    }

    setOpen(false);
  };

  if (!currentAnnouncement?.id) {
    return null;
  }

  const canDismiss = secondsLeft <= 0;
  const hideButtonVisible = Number(currentAnnouncement.hide_for_hours || 0) > 0;
  const announcementBody = (
    <>
      {currentAnnouncement.image && (
        <Box
          component="img"
          src={currentAnnouncement.image}
          alt={currentAnnouncement.title}
          sx={{
            width: "100%",
            maxHeight: { xs: 240, md: 320 },
            objectFit: "cover",
            borderRadius: 2,
            mb: 2,
          }}
        />
      )}
      {currentAnnouncement.message && (
        <Typography
          variant="body1"
          sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
        >
          {currentAnnouncement.message}
        </Typography>
      )}
      {secondsLeft > 0 && (
        <Typography variant="caption" sx={{ display: "block", mt: 1.5 }}>
          {t("Podrás continuar en {{seconds}} segundos", {
            seconds: secondsLeft,
          })}
        </Typography>
      )}
    </>
  );

  const actionButtons = (
    <>
      {currentAnnouncement.link && (
        <Button
          color="inherit"
          startIcon={<OpenInNewRoundedIcon />}
          onClick={() => window.open(currentAnnouncement.link, "_blank")}
        >
          {currentAnnouncement.link_label || t("Ver más")}
        </Button>
      )}
      {hideButtonVisible && (
        <Button
          color="inherit"
          disabled={!canDismiss}
          startIcon={<VisibilityOffRoundedIcon />}
          onClick={() => handleClose({ hideForPeriod: true })}
        >
          {t("No mostrar por ahora")}
        </Button>
      )}
      <Button
        variant="contained"
        disabled={!canDismiss}
        onClick={() => handleClose()}
      >
        {currentAnnouncement.continue_label || t("Continuar")}
      </Button>
    </>
  );

  if (currentAnnouncement.display_mode === "fullscreen") {
    return (
      <Backdrop
        open={open}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 50,
          background:
            "linear-gradient(135deg, rgba(2,6,23,0.92) 0%, rgba(15,23,42,0.85) 100%)",
        }}
      >
        <Box
          sx={{
            width: { xs: "92vw", md: "min(820px, 88vw)" },
            borderRadius: 3,
            overflow: "hidden",
            border: "1px solid rgba(148,163,184,0.35)",
            backgroundColor: "background.paper",
            color: "text.primary",
            boxShadow: "0 20px 55px rgba(2,23,42,0.45)",
            p: { xs: 2, md: 3 },
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5 }}>
            {currentAnnouncement.title}
          </Typography>
          {announcementBody}
          <Box sx={{ mt: 2.5, display: "flex", gap: 1.25, flexWrap: "wrap" }}>
            {actionButtons}
          </Box>
        </Box>
      </Backdrop>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (canDismiss) {
          handleClose();
        }
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{currentAnnouncement.title}</DialogTitle>
      <DialogContent>{announcementBody}</DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>{actionButtons}</DialogActions>
    </Dialog>
  );
};

export default GlobalAnnouncementCenter;
